/**
 * Pure H OS · Phase D · P0-4 · Board Briefs
 *
 * Executive briefs (weekly/monthly/quarterly) backed by the seeded
 * `pureh_briefs` MongoDB collection. Each brief cross-links into
 * /pureh/decisions via a `?open=<id>` query param.
 *
 * Reuses ONLY existing Pure H primitives: Card, SectionHeader,
 * SeverityBadge, TabBar, ListRow, Drawer. No new visual components.
 */
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, SectionHeader, SeverityBadge } from "../components/lib";
import { TabBar, ListRow, Drawer } from "../components/lib.overlay";
import { DocumentActions } from "../components/DocumentActions";
import { BRIEFS, BoardBrief, BriefKind } from "../mocks/briefsData";
import { DECISIONS } from "../mocks/decisionsData";

const KIND_LABEL: Record<BriefKind, string> = {
  weekly: "Weekly", monthly: "Monthly", quarterly: "Quarterly",
};

// Cadence-severity mapping mirrors the pattern established in Reports —
// higher cadence → higher urgency chip. Reuses SeverityBadge.
function kindSeverity(k: BriefKind): "high" | "medium" | "low" {
  if (k === "weekly") return "high";
  if (k === "monthly") return "medium";
  return "low";
}

type Tab = "all" | BriefKind;

export default function Briefs() {
  const [tab, setTab] = useState<Tab>("all");
  const [openId, setOpenId] = useState<string | null>(null);
  const navigate = useNavigate();

  const filtered = useMemo(
    () => (tab === "all" ? BRIEFS : BRIEFS.filter((b) => b.kind === tab)),
    [tab],
  );
  const current = openId ? BRIEFS.find((b) => b.id === openId) ?? null : null;

  const countByKind = useMemo(() => {
    const c: Record<string, number> = {};
    BRIEFS.forEach((b) => { c[b.kind] = (c[b.kind] || 0) + 1; });
    return c;
  }, []);

  // For the drawer: resolve decisionIds → titles from the live DECISIONS.
  const linkedDecisions = useMemo(() => {
    if (!current) return [];
    return current.decisionIds
      .map((id) => DECISIONS.find((d) => d.id === id))
      .filter((d): d is (typeof DECISIONS)[number] => Boolean(d));
  }, [current]);

  return (
    <div className="space-y-6" data-testid="briefs-page">
      <Card testId="brf-filters">
        <TabBar<Tab>
          testId="brf-tabs"
          active={tab}
          onChange={setTab}
          tabs={[
            { key: "all",       label: `All ${BRIEFS.length}` },
            { key: "weekly",    label: `Weekly ${countByKind.weekly ?? 0}` },
            { key: "monthly",   label: `Monthly ${countByKind.monthly ?? 0}` },
            { key: "quarterly", label: `Quarterly ${countByKind.quarterly ?? 0}` },
          ]}
        />
      </Card>

      <Card padded={false} testId="brf-list">
        <div className="p-5 flex items-start justify-between flex-wrap gap-3" style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}>
          <div>
            <SectionHeader
              eyebrow="Board Briefs"
              title={`${filtered.length} brief${filtered.length === 1 ? "" : "s"}`}
            />
            <div className="text-xs" style={{ color: "var(--ph-muted)" }}>
              Period-scoped executive rollups referencing specific decisions.
            </div>
          </div>
          <DocumentActions docKey="board-brief" testId="brf-doc-actions" />
        </div>
        <div className="p-2">
          {filtered.map((b) => (
            <ListRow key={b.id} testId={`brf-${b.id}`}
              onClick={() => setOpenId(b.id)}
              label={
                <span className="flex items-center gap-2">
                  <span className="uppercase tracking-[0.14em] text-[10px]" style={{ color: "var(--ph-muted)" }}>
                    {KIND_LABEL[b.kind]} · {b.period}
                  </span>
                  <span>{b.title}</span>
                </span>
              }
              sublabel={
                <>
                  {b.authoredBy} · published {b.publishedRelative} · {b.decisionIds.length} decisions · {b.risksAcknowledged} risk{b.risksAcknowledged === 1 ? "" : "s"} acknowledged
                </>
              }
              trailing={<SeverityBadge severity={kindSeverity(b.kind)} />}
            />
          ))}
        </div>
      </Card>

      <Drawer open={!!current} onClose={() => setOpenId(null)} testId="brf-drawer"
              title={current ? current.title : ""}>
        {current && (
          <div className="space-y-6">
            <SectionHeader
              eyebrow={`${KIND_LABEL[current.kind]} · ${current.period}`}
              title={current.headline}
            />
            {/* Single-record Board Brief PDF · ORVAN branded */}
            <div className="pb-3 -mt-2" style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}>
              <DocumentActions docKey="board-brief" recordId={current.id} testId={`brf-single-${current.id}`} compact />
            </div>
            <div className="text-xs -mt-4" style={{ color: "var(--ph-muted)" }}>
              {current.authoredBy} · published {current.publishedRelative}
            </div>

            <div className="text-sm leading-relaxed" style={{ color: "var(--ph-text)" }}>
              {current.summary}
            </div>

            <div>
              <div className="text-[10px] uppercase tracking-[0.16em] mb-2" style={{ color: "var(--ph-muted)" }}>
                KPI highlights
              </div>
              <div className="grid grid-cols-3 gap-4">
                {current.kpiHighlights.map((k) => (
                  <div key={k.label} data-testid={`brf-kpi-${k.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                    <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>{k.label}</div>
                    <div className="mt-1 text-base font-semibold tabular-nums" style={{ color: "var(--ph-text)" }}>
                      {k.value}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className="text-[10px] uppercase tracking-[0.16em] mb-2 flex items-center justify-between" style={{ color: "var(--ph-muted)" }}>
                <span>Linked decisions</span>
                <span className="normal-case tracking-normal">{linkedDecisions.length} of {current.decisionIds.length} resolved</span>
              </div>
              <div className="space-y-2">
                {linkedDecisions.map((d) => (
                  <button
                    key={d.id}
                    type="button"
                    data-testid={`brf-dec-link-${d.id}`}
                    onClick={() => {
                      setOpenId(null);
                      navigate(`/pureh/decisions?open=${d.id}`);
                    }}
                    className="w-full text-left p-3 rounded flex items-center justify-between gap-3"
                    style={{
                      background: "var(--ph-surface-well)",
                      border: "1px solid var(--ph-border-subtle)",
                    }}>
                    <div className="min-w-0">
                      <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-muted)" }}>
                        {d.module} · {d.outcome}
                      </div>
                      <div className="mt-0.5 text-sm truncate" style={{ color: "var(--ph-text)" }}>
                        {d.title}
                      </div>
                    </div>
                    <SeverityBadge severity={d.severity} />
                  </button>
                ))}
                {linkedDecisions.length < current.decisionIds.length && (
                  <div className="text-[10px] italic" style={{ color: "var(--ph-muted)" }}>
                    {current.decisionIds.length - linkedDecisions.length} decision id{current.decisionIds.length - linkedDecisions.length === 1 ? "" : "s"} not currently in the log.
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 pt-2" style={{ borderTop: "1px solid var(--ph-border-subtle)" }}>
              <div className="pt-4">
                <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>Cadence</div>
                <div className="mt-2"><SeverityBadge severity={kindSeverity(current.kind)} /></div>
              </div>
              <div className="pt-4">
                <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>Risks acknowledged</div>
                <div className="mt-1 text-sm" style={{ color: "var(--ph-text)" }}>{current.risksAcknowledged}</div>
              </div>
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
}
